import React from 'react';
import { Redirect, useLocation } from 'react-router-dom';

import * as Yup from 'yup';
import { Form, Formik, FormikValues } from 'formik';

import Button from '../../components/buttons/basicButton';
import AuthError from '../../components/errors/AuthError';
import TextInput from '../../components/inputs/textInput';
import NavLink from '../../components/nav/NavLink';
import styled from 'styled-components';

// theme
import aampeTheme from '../../constants/aampeTheme';

import { BaseError } from '../../helpers/BaseError';

// hooks
import useForgotPasswordMutation from '../../hooks/auth/useForgotPasswordMutation';
import { useAuthSession } from '../../hooks/useAuthSession';

import envConfig from '../../services/env-config';

// components
import BaseCard from './baseCard';

const formSchema = Yup.object({
    email: Yup.string().email('Invalid email address').required('Required'),
});

export default function ForgotPasswordCard() {
    const passwordMutation = useForgotPasswordMutation();
    const location = useLocation();
    const handleSubmit = (values: FormikValues) => {
        passwordMutation.mutate({ email: values.email });
    };

    const from = React.useMemo(() => {
        const state = location.state as { from: Location };

        if (state && state.from) {
            return state.from;
        }

        return '/';
    }, [location]);

    const { user: currentUser } = useAuthSession();

    if (currentUser?.name) {
        return <Redirect to={from} />;
    }

    return (
        <BaseCard
            title={
                passwordMutation.isSuccess
                    ? 'Check your email!'
                    : 'Password forgotten?'
            }
        >
            {!passwordMutation.isSuccess && (
                <FormContainer>
                    <Text>Don't worry, it happens to the best of us.</Text>
                    {passwordMutation.isError && (
                        <AuthError
                            errorMsg={
                                passwordMutation.error instanceof BaseError
                                    ? passwordMutation.error.message
                                    : ''
                            }
                        />
                    )}
                    <Formik
                        initialValues={{
                            email: '',
                        }}
                        validationSchema={formSchema}
                        onSubmit={handleSubmit}
                    >
                        {({
                            values,
                            handleChange,
                            handleBlur,
                            errors,
                            touched,
                        }) => (
                            <Form>
                                <TextInput
                                    label="Email:"
                                    name="email"
                                    placeholder="email@domain.com"
                                    value={values?.email}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    error={errors?.email}
                                    touched={touched?.email}
                                    inputStyle={{
                                        width: '100%',
                                    }}
                                    wrapperStyle={{
                                        marginBottom: aampeTheme.spacing(1),
                                    }}
                                />
                                <StyledButton
                                    name="Send a reset link"
                                    type="submit"
                                    isPrimary
                                    isDisabled={passwordMutation.isPending}
                                    isLoading={passwordMutation.isPending}
                                />
                            </Form>
                        )}
                    </Formik>
                    <Footer>
                        Don't need to reset?
                        <Options>
                            <StyledNavLink
                                text="Login"
                                destination={envConfig.AUTH_SERVICE_SIGNIN}
                            />
                        </Options>
                    </Footer>
                </FormContainer>
            )}
            {passwordMutation.isSuccess && (
                <FormContainer>
                    <Text>
                        If your email exists in our system, we've sent you a
                        message with a reset link.
                    </Text>
                    <Footer>
                        Didn't receive the email?
                        <Options>
                            <TextButton
                                onClick={() => {
                                    passwordMutation.reset();
                                }}
                            >
                                Click to resend
                            </TextButton>
                        </Options>
                    </Footer>
                </FormContainer>
            )}
        </BaseCard>
    );
}

const FormContainer = styled.div({
    width: '100%',
});

const StyledNavLink = styled(NavLink)`
    font-size: 16px;
`;

const StyledButton = styled(Button)`
    width: 100%;
    margin: ${aampeTheme.spacing(2, 0)};
    font-size: 16px;
    font-weight: 600;
`;

const Footer = styled.div({
    display: 'flex',
    flexFlow: 'column nowrap',
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
    marginTop: aampeTheme.spacing(1),
});

const Options = styled.div({
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: aampeTheme.spacing(1),
    marginTop: aampeTheme.spacing(2),
});

const Text = styled.div({
    fontSize: '16px',
    lineHeight: '20px',
    color: aampeTheme.colors.neutral09,
    width: '100%',
    marginBottom: aampeTheme.spacing(4),
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
});

const TextButton = styled.a({
    color: aampeTheme.colors.solidA01,
    cursor: 'pointer',
    '&:hover': {
        color: aampeTheme.colors.primary300,
    },
});
